import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Cost } from '@sorare/core/src/components/player/Cost';
import { getPositionInitials } from '@sorare/core/src/lib/positions';

import type { RecentPurchase_anyCardInterface } from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  position: relative;
`;
const PlayerPicture = styled.img`
  mask-image: linear-gradient(to bottom, white 80%, transparent);
`;

export enum TradeType {
  BUY,
  SELL,
}

type Props = { anyCard: RecentPurchase_anyCardInterface };
export const RecentPurchase = ({ anyCard }: Props) => {
  return (
    <Root>
      <Horizontal>
        <PlayerPicture
          height={40}
          src={anyCard.anyPlayer.squaredPictureUrl}
          alt=""
        />
        <Vertical gap={0.5}>
          <BrandS>
            <Ellipsis>{anyCard.anyPlayer.displayName}</Ellipsis>
          </BrandS>
          <Horizontal>
            <LabelM color="var(--c-nd-600)">
              {anyCard.anyPlayer.anyPositions
                .map(position => getPositionInitials(position))
                .join('/')}
            </LabelM>
            <svg
              viewBox="0 0 2 2"
              xmlns="http://www.w3.org/2000/svg"
              width="2.5"
            >
              <circle cx="1" cy="1" r="1" fill="var(--c-nd-600)" />
            </svg>
            <LabelM color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="{age} yo"
                id="RecentPurchase.age"
                values={{ age: anyCard.anyPlayer.age }}
              />
            </LabelM>
          </Horizontal>
        </Vertical>
      </Horizontal>
      <Cost score={anyCard.anyPlayer.averageScore} />
    </Root>
  );
};

RecentPurchase.fragments = {
  anyCardInterface: gql`
    fragment RecentPurchase_anyCardInterface on AnyCardInterface {
      slug
      name
      anyPlayer {
        slug
        age
        displayName
        anyPositions
        averageScore(type: LAST_FIFTEEN_SO5_AVERAGE_SCORE)
        squaredPictureUrl
      }
    }
  ` as TypedDocumentNode<RecentPurchase_anyCardInterface>,
};
